import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";

// Sections in the order they appear on the CV (must match the admin page).
const CV_SECTIONS = [
  "Education",
  "Selected Solo Exhibitions",
  "Selected Group Exhibitions",
  "Residencies & Fellowships",
  "Talks",
  "Teaching & Facilitation",
  "Collections",
  "Selected Bibliography",
  "Press & Publications",
];

// Turn the flat entries from /cv into the { year, items[] } groups the Section
// component renders — grouping consecutive entries that share a year.
function groupBySection(entries) {
  const bySection = {};
  entries.forEach((e) => {
    const groups = (bySection[e.section] = bySection[e.section] || []);
    const year = e.year || "";
    const last = groups[groups.length - 1];
    if (last && last.year === year) last.items.push(e.text);
    else groups.push({ year, items: [e.text] });
  });
  return bySection;
}

// Fallback data — used until the CV is populated from the admin panel.
const education = [
  { year: "2021", items: ["BSc, Covenant University, Ota, Nigeria"] },
];

const soloExhibitions = [
  {
    year: "2026",
    items: [
      "Insight Nigeria, Consulate General of Germany, Lagos, Nigeria",
      "Threads of Light, Threads of Us, Wole Soyinka Center for Culture and Creative Arts, Lagos, Nigeria",
    ],
  },
  {
    year: "2025",
    items: ["Threads of Light, Threads of Us, Our Urban Oasis, London, United Kingdom"],
  },
  {
    year: "2024",
    items: ["In Her Hands, Red Door Gallery, Lagos, Nigeria"],
  },
];

const groupExhibitions = [
  {
    year: "2026",
    items: ["İki Àsà: Two Cultures, One Vision, Yunus Emre Enstitüsü, Abuja, Nigeria"],
  },
  {
    year: "2025",
    items: [
      "Iconic Women: From Everyday Life to Global Heroes, Muhammad Ali Center, Louisville, United States",
    ],
  },
  {
    year: "2020",
    items: ["A Moment in History, digital exhibition, House of ZETA"],
  },
];

const talks = [
  {
    year: "2026",
    items: [
      "TEDxRUN, “Where Your Worlds Collide,” Redeemer’s University, Ede, Nigeria",
      "Artist Talk, Insight Nigeria, Consulate General of Germany, Lagos, Nigeria",
    ],
  },
];

const collections = [
  { year: "", items: ["Works held in private collections in Nigeria and the United States"] },
];

const press = [
  {
    year: "2026",
    items: [
      "Nana Ocran, “Before I’m a Photographer, I’m a Human,” People’s Stories Project.",
      "Ayşenur Sare Tok, “Yunus Emre Institute Brings Türkiye and Nigeria Together Through Art,” TRT Afrika.",
    ],
  },
  {
    year: "2025",
    items: [
      "Gerda Krivaite, “Threads of Light, Threads of Us: Sope Adelaja’s Meditation on Connection,” Our Culture Mag.",
      "Grace Adepoju, “Nigerian Photographer Sope Adelaja Brings 'Threads of Light, Threads of Us’ Exhibition to London This December,” Prazzle Arts.",
      "“Making the Invisible Visible: Portraits of a Hotter Planet,” Earth Journalism Network / Global Heat Health Information Network.",
      "Ayo Lawal, “More Than Pretty Pictures: Sope Adelaja on Photography, Heritage and Humanity,” ThisDay Style.",
      "Diane Cole, “Portraits of Women Who ‘Shine a Light’: From an ‘Analog’ Astronaut to a Watermelon Farmer,” NPR.",
    ],
  },
  {
    year: "2024",
    items: [
      "Marc Silver and Ben de la Cruz, “Great Global Photos From 2024: These Images Delighted Us, Touched Us, Dazzled Us,” NPR.",
      "Chidirim Ndeche, “In Her Hands: Sope Adelaja debuts solo art exhibition,” The Guardian Life.",
      "“‘In Her Hands’ exhibition: Sope Adelaja’s artistic vision of nurture and sustainability,” The Guardian Life.",
      "Emily Sohn, “Photos: How 9 Families Cope When They Can’t Afford 3 Healthy Meals a Day for the Kids,” NPR.",
    ],
  },
  {
    year: "2020",
    items: [
      "“Anny Robert, Lex Ash, Tobbinator And More To Showcase At A Moment In History,” The Guardian Life",
    ],
  },
];

// A single CV section, matching the PDF exactly: bold uppercase heading, then each
// year on its own line with its entries stacked beneath it (no bullets, no em-dash).
const Section = ({ title, groups }) => (
  <section className="mt-8">
    <h2 className="text-[15px] font-bold uppercase text-[#e7ddd3] mb-3">{title}</h2>
    <div className="space-y-4">
      {groups.map((group, gi) => (
        <div key={group.year || gi}>
          {group.year && <p className="text-[15px] text-[#8a8175] mb-1">{group.year}</p>}
          <div className="space-y-3">
            {group.items.map((item, i) => (
              <p key={i} className="text-[15px] leading-relaxed text-[#cdc2ad] whitespace-pre-line">
                {item}
              </p>
            ))}
          </div>
        </div>
      ))}
    </div>
  </section>
);

// Fallback used until the CV is populated from the admin panel.
const FALLBACK_SECTIONS = {
  "Education": education,
  "Selected Solo Exhibitions": soloExhibitions,
  "Selected Group Exhibitions": groupExhibitions,
  "Talks": talks,
  "Collections": collections,
  "Press & Publications": press,
};

const FALLBACK_META = {
  name: "Sope Adelaja",
  born: "Born in 2000 in Lagos, Nigeria",
  resides: "Resides and works in Lagos, Nigeria",
  email: "hello@sopeadelaja.com",
  website: "www.sopeadelaja.com",
  instagram: "@sopeadelaja",
};

const CV = () => {
  const [meta, setMeta] = useState(null);
  const [sections, setSections] = useState(null); // null = not loaded yet

  useEffect(() => {
    const fetchCv = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/cv`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.meta) setMeta(data.meta);
        if (data.entries && data.entries.length > 0) {
          setSections(groupBySection(data.entries));
        }
      } catch (error) {
        console.error("Error fetching CV:", error);
      }
    };
    fetchCv();
  }, []);

  // Fall back to the built-in data until the CV is managed from the admin panel.
  const m = { ...FALLBACK_META, ...(meta || {}) };
  const secs = sections || FALLBACK_SECTIONS;

  const websiteHref = m.website
    ? (m.website.startsWith("http") ? m.website : `https://${m.website}`)
    : "";
  const instagramHandle = (m.instagram || "").replace(/^@/, "");

  return (
    <>
      <Helmet>
        <title>Sope Adelaja | CV</title>
        <meta
          name="description"
          content="Curriculum Vitae of Sope Adelaja — artist and photographer based in Lagos, Nigeria."
        />
      </Helmet>

      <div className="bg-[#252525] min-h-screen w-full font-sans text-[#cdc2ad]">
        <div className="max-w-3xl mx-auto px-6 md:px-8 py-14 md:py-20">
          {/* Header */}
          <header className="mb-2">
            <h1 className="text-[17px] font-bold uppercase text-[#e7ddd3] mb-1">{m.name}</h1>
            {m.born && <p className="text-[15px] text-[#b7a78b]">{m.born}</p>}
            {m.resides && <p className="text-[15px] text-[#b7a78b]">{m.resides}</p>}
          </header>

          {CV_SECTIONS.filter((title) => (secs[title] || []).length > 0).map((title) => (
            <Section key={title} title={title} groups={secs[title]} />
          ))}

          {/* Contact */}
          <section className="mt-8">
            <h2 className="text-[15px] font-bold uppercase text-[#e7ddd3] mb-3">Contact</h2>
            <div className="space-y-1 text-[15px] text-[#cdc2ad]">
              {m.email && (
              <p>
                Email:{" "}
                <a href={`mailto:${m.email}`} className="text-[#efefcb] hover:underline">
                  {m.email}
                </a>
              </p>
              )}
              {m.website && (
              <p>
                Website:{" "}
                <a
                  href={websiteHref}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#efefcb] hover:underline"
                >
                  {m.website}
                </a>
              </p>
              )}
              {m.instagram && (
              <p>
                Instagram:{" "}
                <a
                  href={`https://www.instagram.com/${instagramHandle}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#efefcb] hover:underline"
                >
                  {m.instagram}
                </a>
              </p>
              )}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default CV;
